import { Component, OnInit, Input, ViewChild, ChangeDetectorRef, OnDestroy, inject } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subscription } from 'rxjs';

import { ByocService } from '../../../../service/byoc.service';
import { ByocEpisodeService } from '../../../service/episode.service';

import { Course } from '@app/core/model/library.model';

import { Pagination } from '@app/core/model/http-pagination.model';
import { LazyLoad } from '@app/core/model/pagination.model';
import { PerfectScrollbarDirective } from '@app/shared/perfect-scrollbar/perfect-scrollbar.directive';
import { control } from '../../../../utils';

@Component({
    selector: 'app-byoc-episode-step-course-list',
    templateUrl: './list.component.html',
    styleUrls: ['./list.component.scss'],
    standalone: false
})
export class ByocEpisodeStepCourseListComponent implements OnInit, OnDestroy {
    @ViewChild('ps', { read: PerfectScrollbarDirective, static: true })
    psRef?: PerfectScrollbarDirective;

    @Input()
    searchFormControl!: UntypedFormControl;

    @Input()
    originControl!: UntypedFormControl;

    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly byocService = inject(ByocService);
    private readonly byocEpisodeService = inject(ByocEpisodeService);

    /**
     * Search Delay
     */
    delay: any;

    /**
     * Courses
     */
    courses: Course[] = [];

    /**
     * Is Searching flag
     */
    searching = false;

    /**
     * Subscriptions
     */
    private subscriptions: Subscription = new Subscription();

    /**
     * Search Subscription
     */
    private subscriptionSearch: Subscription = new Subscription();

    /**
     * lazyLoad
     */
    private lazyLoad: LazyLoad = {
        offset: {
            prefixPosition: 0,
            suffixPosition: 0,
        },
        totalItems: 0,
        limit: 50,
    };

    ngOnInit() {
        this.search();
        this.subscriptions.add(
            this.searchFormControl.valueChanges.subscribe(() => {
                this.search();
            }),
        );
        this.subscriptions.add(
            this.originControl.valueChanges.subscribe(() => {
                this.search();
            }),
        );
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
        if (this.subscriptionSearch) {
            this.subscriptionSearch.unsubscribe();
        }
        if (this.delay) {
            clearTimeout(this.delay);
        }
    }

    /**
     * Course ID Control
     */
    get courseIdControl() {
        return control(this.byocEpisodeService.form.get('courseId'));
    }

    get scrollbarYEnabled(): boolean {
        return this.psRef?.ps?.scrollbarYActive ?? false;
    }

    /**
     * Y Reached end event
     */
    psYReachEnd() {
        this.append();
    }

    /**
     * No result found
     */
    get noResults(): boolean {
        return !this.searching && this.lazyLoad.totalItems === 0;
    }

    /**
     * Search
     */
    private search(): void {
        this.psRef?.scrollToTop();

        // clear value
        if (this.courseIdControl.value !== '') {
            this.courseIdControl.patchValue('');
        }

        // unsubscribe from active
        if (this.subscriptionSearch) {
            this.subscriptionSearch.unsubscribe();
        }

        // clear timtout from active
        if (this.delay) {
            clearTimeout(this.delay);
        }

        // reset lazyLoad
        this.resetLazyLoad();

        this.searching = true;
        this.changeDetectorRef.detectChanges();

        this.delay = setTimeout(() => {
            this.subscriptionSearch = this.byocService
                .searchCourse(this.searchFormControl.value, this.originControl.value, this.lazyLoad.limit, this.lazyLoad.offset.suffixPosition)
                .subscribe((data: { courses: Course[]; pagination: Pagination }) => {
                    this.courses = data.courses;
                    this.lazyLoad.totalItems = data.pagination.totalItems;
                    this.lazyLoad.offset.suffixPosition += data.courses.length;
                    this.searching = false;
                    this.changeDetectorRef.detectChanges();
                    this.psRef?.ps.update();
                    // need to refresh
                    setTimeout(() => {
                        this.changeDetectorRef.detectChanges();
                    }, 0);
                });
        }, 250);
    }

    /**
     * Append Search
     */
    private append(): void {
        // don't run when already saerching
        if (this.searching) {
            return;
        }

        // don't run when all loaded
        if (this.courses.length === this.lazyLoad.totalItems) {
            return;
        }

        this.searching = true;
        this.changeDetectorRef.detectChanges();

        this.subscriptionSearch = this.byocService
            .searchCourse(this.searchFormControl.value, this.originControl.value, this.lazyLoad.limit, this.lazyLoad.offset.suffixPosition)
            .subscribe((data: { courses: Course[]; pagination: Pagination }) => {
                this.lazyLoad.totalItems = data.pagination.totalItems;
                this.lazyLoad.offset.suffixPosition += data.courses.length;
                this.courses = this.courses.concat(data.courses);
                this.searching = false;
                this.changeDetectorRef.detectChanges();
                this.psRef?.ps?.update();
                // need to refresh
                setTimeout(() => {
                    this.changeDetectorRef.detectChanges();
                }, 0);
            });
    }

    private resetLazyLoad(): void {
        this.lazyLoad = {
            offset: {
                prefixPosition: 0,
                suffixPosition: 0,
            },
            totalItems: 0,
            limit: 50,
        };
    }
}
